import type {
  StudioDepartureKitStatusV1,
  StudioIntakeSummaryV1,
  StudioPackageResolutionV1,
} from "@enrein/shared-contracts";
import type { SitePackageId } from "@enrein/shared-contracts";
import type { StudioApiClient } from "../../api.js";
import type { PreviewOpenGuardInput } from "../studio-shared/preview-open-guard.js";
import type { ClientShareGuardInput } from "../studio-shared/share-guard.js";
import type { PackageEligibilityBlocked } from "../studio-shared/workflow/studio-workflow.js";
import type { CommandCenterStatCard, SimpleProjectPhaseState } from "../studio-shared/orchestration/project-phase.js";
import type { V10ControlRoomSection, V10ProjectMode } from "./adapters/v10-phase-model.js";
import type { V10SectionProgressStatus } from "./adapters/v10-project-adapters.js";
import { v10CloseSheet, v10NewWorkSheet } from "./adapters/v10-button-endpoints.js";
import type { V10InboxRow, V10ProjectListItem, V10QueueCard, V10ScanRow } from "./adapters/v10-fixtures.js";
import type { V10NewProjectFormState } from "./adapters/v10-new-project-form.js";
import { V10Sidebar } from "./chrome/V10Sidebar.js";
import { V10Topbar } from "./chrome/V10Topbar.js";
import { V10NewWorkSheet } from "./components/V10TodayEntryButtons.js";
import type { V10Navigate, V10RouteState } from "./routing/v10-routing.js";
import type { V10SidebarView } from "./routing/v10-views.js";
import { V10ClientPortalInboxView } from "./views/V10ClientPortalInboxView.js";
import { V10CommandCenterView } from "./views/V10CommandCenterView.js";
import { V10ProjectRoomView } from "./views/V10ProjectRoomView.js";
import { V10ProjectsView } from "./views/V10ProjectsView.js";
import { V10ScansProofView } from "./views/V10ScansProofView.js";
import { V10PortfolioView } from "./views/V10PortfolioView.js";
import { V10SettingsView } from "./views/V10SettingsView.js";
import type { StudioQaIssue } from "./components/V10ResolveIssuesPanel.js";
import type { CanonicalPresentationModelV1, CanonicalPrimaryAction } from "@enrein/shared-contracts";

export interface V10QueueSections {
  readonly continueCards: readonly V10QueueCard[];
  readonly attentionCards: readonly V10QueueCard[];
  readonly clientRequestCards: readonly V10QueueCard[];
  readonly stuckCards: readonly V10QueueCard[];
  readonly readyCards: readonly V10QueueCard[];
}

function resolveSidebarView(route: V10RouteState): V10SidebarView {
  if (route.view === "project") return "projects";
  if (
    route.view === "today" ||
    route.view === "projects" ||
    route.view === "portfolio" ||
    route.view === "inbox" ||
    route.view === "scans" ||
    route.view === "settings"
  ) {
    return route.view;
  }
  return "today";
}

export function V10Shell({
  route,
  onNavigate,
  onOpenProject,
  onSectionNavigate,
  workflowBusy,
  validationIssues,
  qaIssues,
  qaWarnings,
  importQueueAvailable,
  newProjectForm,
  onNewProjectFormChange,
  onCreateProject,
  queueSections,
  portfolioSignals,
  portfolioLoading,
  portfolioError,
  portfolioSummary,
  portfolioSummaryLoading,
  portfolioSummaryError,
  onStatNavigate,
  startHere = null,
  onStartHereAction,
  projectListItems,
  projectsLoading,
  projectsError,
  inboxRows,
  scanRows,
  scansLoading,
  scansError,
  onRunScan,
  onOpenRelaunchSheet,
  deployEnv,
  configAgencyId,
  phaseState,
  sectionProgress,
  projectLoading,
  projectLoadError,
  businessName,
  intakeStatusLabel,
  intakeSummary,
  changeRequestPreview,
  previewUrl,
  previewOpenGuardInput,
  clientShareGuardInput,
  onWarnShareAcknowledgedChange,
  studioPreviewHtml,
  previewError,
  previewRoutes,
  activePreviewRoute,
  onPreviewRouteChange,
  sitePackageId,
  requestedSitePackageId,
  builtSitePackageId,
  packageEligibilityBlocked,
  packageResolution,
  onBuildWithPackage,
  onEnrichProfile,
  exportRunId,
  handoffError,
  departureKit,
  liveCarePresentation = null,
  onPrimaryAction,
  onCopyClientLink,
  onShareClientLinkFromPreview,
  onCopyPreviewLink,
  onOpenPreview,
  onOpenPortal,
  api,
  onImportQueueChanged,
}: {
  readonly route: V10RouteState;
  readonly onNavigate: V10Navigate;
  readonly onOpenProject: V10Navigate;
  readonly onSectionNavigate: V10Navigate;
  readonly workflowBusy: boolean;
  readonly validationIssues: readonly string[];
  readonly qaIssues: readonly StudioQaIssue[];
  readonly qaWarnings: readonly string[];
  readonly importQueueAvailable: boolean;
  readonly newProjectForm: V10NewProjectFormState;
  readonly onNewProjectFormChange: (form: V10NewProjectFormState) => void;
  readonly onCreateProject: (mode: V10ProjectMode) => void;
  readonly queueSections: V10QueueSections;
  readonly portfolioSignals: readonly { readonly label: string; readonly value: string; readonly navigateTo: CommandCenterStatCard["navigateTo"] }[];
  readonly portfolioLoading: boolean;
  readonly portfolioError: string | null;
  readonly portfolioSummary: import("@enrein/shared-contracts").AgencyPortfolioSummaryV1 | null;
  readonly portfolioSummaryLoading: boolean;
  readonly portfolioSummaryError: string | null;
  readonly onStatNavigate: (target: NonNullable<CommandCenterStatCard["navigateTo"]>) => void;
  readonly startHere?: CanonicalPresentationModelV1 | null;
  readonly onStartHereAction?: (action: CanonicalPrimaryAction) => void;
  readonly projectListItems: readonly V10ProjectListItem[];
  readonly projectsLoading: boolean;
  readonly projectsError: string | null;
  readonly inboxRows: readonly V10InboxRow[];
  readonly scanRows: readonly V10ScanRow[];
  readonly scansLoading: boolean;
  readonly scansError: string | null;
  readonly onRunScan: (url: string) => void;
  readonly onOpenRelaunchSheet: (scanUrl?: string) => void;
  readonly deployEnv: string;
  readonly configAgencyId: string;
  readonly phaseState:
    | (SimpleProjectPhaseState & {
        readonly facts: SimpleProjectPhaseState["facts"];
        readonly heroPresentation?: CanonicalPresentationModelV1 | null;
      })
    | null;
  readonly sectionProgress: Record<V10ControlRoomSection, V10SectionProgressStatus> | null;
  readonly projectLoading: boolean;
  readonly projectLoadError: string | null;
  readonly businessName: string;
  readonly intakeStatusLabel: string;
  readonly intakeSummary: StudioIntakeSummaryV1 | null;
  readonly changeRequestPreview: string | null;
  readonly previewUrl: string | null;
  readonly previewOpenGuardInput: PreviewOpenGuardInput;
  readonly clientShareGuardInput: ClientShareGuardInput;
  readonly onWarnShareAcknowledgedChange: (acknowledged: boolean) => void;
  readonly studioPreviewHtml: string | null;
  readonly previewError: string | null;
  readonly previewRoutes: readonly string[];
  readonly activePreviewRoute: string;
  readonly onPreviewRouteChange: (route: string) => void;
  readonly sitePackageId: string | null;
  readonly requestedSitePackageId: string;
  readonly builtSitePackageId: string | null;
  readonly packageEligibilityBlocked: PackageEligibilityBlocked | null;
  readonly packageResolution: StudioPackageResolutionV1 | null;
  readonly onBuildWithPackage: (packageId: SitePackageId) => void;
  readonly onEnrichProfile: () => void;
  readonly exportRunId: string | null;
  readonly handoffError: string | null;
  readonly departureKit: StudioDepartureKitStatusV1 | null;
  readonly liveCarePresentation?: CanonicalPresentationModelV1 | null;
  readonly onPrimaryAction: (actionId: string) => void;
  readonly onCopyClientLink: () => void;
  readonly onShareClientLinkFromPreview: () => void;
  readonly onCopyPreviewLink: () => void;
  readonly onOpenPreview: () => void;
  readonly onOpenPortal: () => void;
  readonly api: StudioApiClient;
  readonly onImportQueueChanged?: () => void;
}) {
  const sidebarView = resolveSidebarView(route);
  const sheetMode: V10ProjectMode = route.mode ?? "DEFAULT_GENERATION";

  const openNewWork = (mode?: V10ProjectMode) => {
    onNavigate(mode ? v10NewWorkSheet(mode) : v10NewWorkSheet());
  };

  return (
    <div className="v10-shell">
      <a className="v10-skip-link" href="#v10-main">
        Skip to main content
      </a>
      <V10Sidebar
        activeView={sidebarView}
        onNavigate={(view) => onNavigate({ view, sheet: null, intake: null })}
        onNewWork={() => openNewWork()}
      />
      <div className="v10-workspace">
        <V10Topbar view={route.view} />
        <main id="v10-main" className="v10-main">
          {route.view === "today" ? (
            <V10CommandCenterView
              onNavigate={onNavigate}
              onOpenProject={onOpenProject}
              queueSections={queueSections}
              portfolioSignals={portfolioSignals}
              portfolioLoading={portfolioLoading}
              portfolioError={portfolioError}
              onStatNavigate={onStatNavigate}
              startHere={startHere}
              onStartHereAction={onStartHereAction}
            />
          ) : null}
          {route.view === "projects" ? (
            <V10ProjectsView
              onNavigate={onNavigate}
              onOpenProject={onOpenProject}
              projects={projectListItems}
              loading={projectsLoading}
              error={projectsError}
            />
          ) : null}
          {route.view === "portfolio" ? (
            <V10PortfolioView
              summary={portfolioSummary}
              loading={portfolioSummaryLoading}
              error={portfolioSummaryError}
              onNavigate={onNavigate}
            />
          ) : null}
          {route.view === "inbox" ? (
            <V10ClientPortalInboxView onNavigate={onNavigate} onOpenProject={onOpenProject} rows={inboxRows} />
          ) : null}
          {route.view === "scans" ? (
            <V10ScansProofView
              intake={route.intake}
              scanRows={scanRows}
              loading={scansLoading}
              error={scansError}
              busy={workflowBusy}
              api={api}
              onRunScan={onRunScan}
              onOpenRelaunchSheet={onOpenRelaunchSheet}
              onAttachExternalProof={(url) => {
                onNewProjectFormChange({ ...newProjectForm, externalUrl: url });
                openNewWork("EXTERNAL_PROOF_ONLY");
              }}
            />
          ) : null}
          {route.view === "settings" ? (
            <V10SettingsView deployEnv={deployEnv} agencyId={configAgencyId} api={api} />
          ) : null}
          {route.view === "project" ? (
            <V10ProjectRoomView
              route={route}
              onNavigate={onNavigate}
              onSectionNavigate={onSectionNavigate}
              phaseState={phaseState}
              sectionProgress={sectionProgress}
              projectLoading={projectLoading}
              projectLoadError={projectLoadError}
              businessName={businessName}
              intakeStatusLabel={intakeStatusLabel}
              intakeSummary={intakeSummary}
              changeRequestPreview={changeRequestPreview}
              previewUrl={previewUrl}
              previewOpenGuardInput={previewOpenGuardInput}
              clientShareGuardInput={clientShareGuardInput}
              onWarnShareAcknowledgedChange={onWarnShareAcknowledgedChange}
              studioPreviewHtml={studioPreviewHtml}
              previewError={previewError}
              previewRoutes={previewRoutes}
              activePreviewRoute={activePreviewRoute}
              onPreviewRouteChange={onPreviewRouteChange}
              sitePackageId={sitePackageId}
              requestedSitePackageId={requestedSitePackageId}
              builtSitePackageId={builtSitePackageId}
              packageEligibilityBlocked={packageEligibilityBlocked}
              packageResolution={packageResolution}
              onBuildWithPackage={onBuildWithPackage}
              onEnrichProfile={onEnrichProfile}
              exportRunId={exportRunId}
              handoffError={handoffError}
              departureKit={departureKit}
              liveCarePresentation={liveCarePresentation}
              workflowBusy={workflowBusy}
              validationIssues={validationIssues}
              qaIssues={qaIssues}
              qaWarnings={qaWarnings}
              importQueueAvailable={importQueueAvailable}
              onPrimaryAction={onPrimaryAction}
              onCopyClientLink={onCopyClientLink}
              onShareClientLinkFromPreview={onShareClientLinkFromPreview}
              onCopyPreviewLink={onCopyPreviewLink}
              onOpenPreview={onOpenPreview}
              onOpenPortal={onOpenPortal}
              api={api}
              onImportQueueChanged={onImportQueueChanged}
            />
          ) : null}
        </main>
      </div>
      {route.sheet === "new-work" ? (
        <V10NewWorkSheet
          selectedMode={sheetMode}
          form={newProjectForm}
          busy={workflowBusy}
          validationIssues={validationIssues}
          onModeChange={(mode) => onNavigate({ mode })}
          onFormChange={onNewProjectFormChange}
          onCreate={() => onCreateProject(sheetMode)}
          onCancel={() => onNavigate(v10CloseSheet())}
        />
      ) : null}
    </div>
  );
}
